import { Dialog, DialogContent } from "@/shared";
import Loader from '@/shared/components/ui/Loader';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { useTranslation } from "react-i18next";
import useClientTasksSyncBoard from "../hooks/useClientTasksSyncBoard";
import { TasksByDate } from "../types/ClientTasksBoardTasks";

const ClientTasksSyncBoard: React.FC = () => {
  const { t } = useTranslation();

  const {
    processed,
    filteredClientsForModal,
    modalConfig,
    isLoading,
    isMerged,
    setIsMerged,
    setModalConfig,
    closeDialog,
    nextWeek,
    prevWeek
  } = useClientTasksSyncBoard();

  const renderTaskGrid = (date: string, tasksSource: TasksByDate, isMergedMode: boolean) => {
    const tasks = tasksSource?.[date] || [];

    const getColor = (color: string) => {
      switch (color) {
        case 'yellow':
          return "rgb(245, 247, 196)";
        case 'blue':
          return "rgb(219, 235, 255)";
        case 'red':
          return "rgb(255, 187, 187)";
        case 'green':
          return "rgb(189, 244, 203)";
        case 'orange':
          return "rgb(255, 215, 179)";
        case 'dark grey':
          return "#cfd8dc";
        case 'lime':
          return "#e6ee9c";
        case 'purple':
          return 'rgb(223, 176, 255)';
        case 'deep orange':
          return '#ffab91';
        case 'pink':
          return '#f48fb1';
        case 'teal':
          return '#80cbc4';
        case 'cyan':
          return '#b2ebf2';
        case 'light green':
          return '#dcedc8';
        case 'amber':
          return '#ffe082';
        case 'brown':
          return '#d7ccc8';
        case 'deep purple':
          return '#ce93d8';
        case 'indigo':
          return '#9fa8da';
        default:
          return 'white';
      }
    };

    return (
      <td className=" p-3 border-b border-gray-100 bg-white" >
        <div className='grid grid-cols-2 md:flex md:flex-wrap gap-2'>
          {tasks?.map((task, index) => (
            <div key={index}
              className="relative flex flex-col gap-1 items-center justify-center p-2 rounded-lg shadow-sm border border-black/5 "
              style={{
                backgroundColor: getColor(String(task?.color).toLowerCase()),
                width: window.innerWidth < 768 ? '100%' : '170px',
                height: '50px',
              }}
            >
              {isMergedMode && (
                <div className="absolute top-1 right-1 flex gap-1">
                  {task.originMatched && task.originMatched && (
                    <span className="w-2 h-2 bg-green-500 rounded-full border border-white shadow-sm" title="Not in Itqan"></span>
                  )}
                </div>
              )}
              <span className="text-[12px] font-bold leading-tight text-gray-800 text-center line-clamp-2">
                {task.clientName}
              </span>
            </div>
          ))}
        </div>
      </td>
    )
  }

  return (
    <div className="p-6 bg-gray-50/50 min-h-screen">
      {isLoading ? <Loader /> : (
        <>
          <div className="flex justify-between mb-6 bg-white p-2 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center gap-2">
              <button
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
                onClick={prevWeek}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                </svg>
              </button>
              <button
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
                onClick={nextWeek}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                </svg>
              </button>
            </div>
            <div className="flex items-center gap-3">
              <button
                className="px-4 py-2 text-sm bg-white border border-gray-200 hover:border-amber-500 hover:text-amber-600 rounded-lg font-bold transition-all shadow-sm"
                onClick={() => setModalConfig({ isOpen: true, type: 'empty' })}
              >
                {t("clients_without_tasks")}
              </button>
              <button
                className="px-4 py-2 text-sm bg-white border border-gray-200 hover:border-amber-500 hover:text-amber-600 rounded-lg font-bold transition-all shadow-sm"
                onClick={() => setModalConfig({ isOpen: true, type: 'only_in_itqan' })}
              >
                {t("only_in_itqan")}
              </button>
              <button
                className="px-4 py-2 text-sm bg-white border border-gray-200 hover:border-blue-500 hover:text-blue-600 rounded-lg font-bold transition-all shadow-sm"
                onClick={() => setModalConfig({ isOpen: true, type: 'multi' })}
              >
                {t("clients_with_multi_tasks")}
              </button>
              <button
                className={`px-4 py-2 text-sm rounded-lg font-bold transition-all shadow-sm border ${isMerged
                  ? "bg-purple-100 border-purple-300 text-purple-700"
                  : "bg-white border-gray-200 text-gray-600 hover:border-purple-500"
                  }`}
                onClick={() => {
                  const newValue = !isMerged;
                  localStorage.setItem('isMerged', String(newValue));
                  setIsMerged(!isMerged)
                }}
              >
                {isMerged ? t("view_separated") : t("view_merged")}
              </button>
            </div>

          </div>
          {isMerged ? (
            <div className="mb-10">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2 text-purple-700">
                <span className="w-2 h-6 bg-purple-500 rounded-full"></span>
                {t("all_tasks_merged")}
              </h2>
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-md">
                <div className="max-w-full overflow-x-auto">
                  <table className="w-full text-right border-separate border-spacing-0">
                    <tbody>
                      {processed.weekDays.map((date) => {
                        const allTasksForToday = {
                          [date]: [
                            ...(processed.matchedTasks?.[date] || []).map(t => ({ ...t, originMatched: true })),
                            ...(processed.unmatchedTasks?.[date] || []).map(t => ({ ...t, originMatched: false }))
                          ]
                        };
                        return (
                          <tr key={date} className="hover:bg-gray-50/30 transition-colors">
                            <td className="p-4 text-center bg-gray-50/50 border-l border-b border-gray-200 font-bold sticky right-0 z-10 w-[100px]">
                              <div className="text-sm text-blue-800">{date}</div>
                              <div className="text-[11px] text-gray-400 font-normal uppercase">
                                {format(new Date(date), 'EEEE', { locale: ar })}
                              </div>
                            </td>
                            {renderTaskGrid(date, allTasksForToday, true)}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>)
            : (
              <>
                <div className="mb-10">
                  <h2 className="text-lg font-bold mb-4 flex items-center gap-2 text-green-700">
                    <span className="w-2 h-6 bg-green-500 rounded-full"></span>
                    {t("clients_associated_with_Itqan_system")}
                  </h2>
                  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-md">
                    <div className="max-w-full overflow-x-auto">
                      <table className="w-full text-right border-separate border-spacing-0">
                        <tbody>
                          {processed.weekDays.map((date) => (
                            <tr key={date} className="hover:bg-amber-50/30 transition-colors">
                              <td className="p-4 text-center bg-gray-50/50 border-l border-b border-gray-200 font-bold sticky right-0 z-10 w-[100px]">
                                <div className="text-sm text-blue-800 whitespace-nowrap overflow-hidden">{date}</div>
                                <div className="text-[11px] text-gray-400 font-normal uppercase">{format(new Date(date), 'EEEE', { locale: ar })}</div>
                              </td>
                              {renderTaskGrid(date, processed.matchedTasks, false)}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="text-lg font-bold mb-4 flex items-center gap-2 text-amber-700">
                    <span className="w-2 h-6 bg-amber-500 rounded-full"></span>
                    {t("clients_are_not_in_Itqan")}
                  </h2>
                  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-md">
                    <div className="max-w-full overflow-x-auto">
                      <table className="w-full text-right border-separate border-spacing-0">
                        <tbody>
                          {processed.weekDays.map((date) => (
                            <tr key={date} className="hover:bg-amber-50/30 transition-colors">
                              <td className="p-4 text-center bg-gray-50/50 border-l border-b border-gray-200 font-bold sticky right-0 z-10 w-[100px]">
                                <div className="text-sm text-blue-800 whitespace-nowrap overflow-hidden">{date}</div>
                                <div className="text-[11px] text-gray-400 font-normal uppercase">{format(new Date(date), 'EEEE', { locale: ar })}</div>
                              </td>
                              {renderTaskGrid(date, processed.unmatchedTasks, false)}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
        </>
      )}


      <Dialog open={modalConfig.isOpen} onOpenChange={closeDialog}>
        <DialogContent className="max-w-3xl rounded-2xl" onClose={closeDialog}>
          <div className="p-2">
            <h2 className="text-xl font-black mb-6 text-gray-800 border-b pb-4">

              {modalConfig.type === 'multi'
                ? t("clients_have_more_than_one_task")
                : modalConfig.type === 'only_in_itqan'
                  ? t("only_in_itqan")
                  : t("clients_have_no_tasks")
              }

            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 overflow-y-auto max-h-[60vh] p-1">
              {filteredClientsForModal?.length > 0 ? (
                filteredClientsForModal.map((client) => (
                  <div
                    key={client.client_id}
                    className="flex items-center gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-white hover:shadow-md hover:border-blue-200 transition-all group"
                  >
                    <div
                      className="w-3 h-3 rounded-full shrink-0 group-hover:scale-125 transition-transform"
                      style={{ backgroundColor: client.task?.color || '#ccc' }}
                    />
                    <span className="text-sm font-bold text-gray-700 group-hover:text-blue-700 transition-colors">
                      {modalConfig.type === 'multi'
                        ? client?.client_name
                        : modalConfig.type === 'empty'
                          ? client?.client_name
                          : client.account_name
                      }
                    </span>
                  </div>
                ))
              ) : (
                <div className="col-span-full py-16 flex flex-col items-center justify-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                  <div className="bg-white p-4 rounded-full shadow-sm mb-3 text-gray-300">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m6 4.125l2.25 2.25m0 0l2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25-2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                    </svg>
                  </div>
                  <p className="text-gray-400 font-medium">{t("No data available")}</p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientTasksSyncBoard; 